<template>
	<Modal
		:max-width="'max-w-[720px]'"
		:hide-actions="true"
		:allow-close="false"
		:remove-padding="true"
		rounded-size="rounded-[20px]"
		:prevent-scrolling="false"
		@modal:close="emit('close')"
	>
		<template #body>
			<div class="relative">
				<PaywallHeader />
				<button
					type="button"
					class="absolute top-3 right-3 rounded-full p-1 hover:bg-black/5"
					aria-label="Close"
					@click="emit('close')"
				>
					<XMarkIcon class="size-6 text-won-dark-blue" />
				</button>
			</div>

			<div class="px-6 pt-5 pb-6 font-[Inter] text-won-blue">
				<h2 class="text-center text-[22px] leading-tight font-bold">
					{{ headline }}
				</h2>
				<p
					v-if="subcopy"
					class="mt-2 text-center text-sm text-won-dark-blue"
				>
					{{ subcopy }}
				</p>
			</div>

			<div class="border-t border-gray-200 md:grid md:grid-cols-2">
				<div class="hidden md:block">
					<BasicPlanColumn
						cta-label="Stay On Free"
						:collapsible="false"
						@cta="emit('stay-free')"
					/>
				</div>
				<PremiumPlanColumn
					mode="offer"
					:collapsible="false"
					@select="onUpgrade"
				/>
			</div>

			<div class="flex justify-center px-6 pt-2 pb-5 md:hidden">
				<button
					type="button"
					class="text-sm font-medium text-won-green hover:underline"
					@click="emit('stay-free')"
				>
					Stay on Free
				</button>
			</div>
		</template>
	</Modal>
</template>

<script setup>
import { onMounted } from 'vue';

import { XMarkIcon } from '@heroicons/vue/24/outline';

import Modal from '../components/modal.vue';
import BasicPlanColumn from '../components/purchase/pricing/basic-plan-column.vue';
import PremiumPlanColumn from '../components/purchase/pricing/premium-plan-column.vue';
import PaywallHeader from '../icons/first-application-paywall-header.vue';
import { useSubscriptionPlanStore } from '../store/subscription.store';
import { usePremiumNudgeStore } from '../store/nudge-premium.store';

/**
 * The shared WonsultingAI paywall: branded header, a context-specific headline and
 * subcopy, and Basic next to the Premium / God Mode column with its billing-cycle toggle.
 * Mobile shows only the paid column, with "Stay on Free" as a link beneath it (EN-1027).
 *
 * The caller owns visibility (`v-if`) and what each action means, so the same modal can
 * back several upgrade moments. Upgrading is handled here because the handoff to the
 * global purchase modal is identical everywhere; the `upgrade` event is emitted afterwards
 * so callers can still do their own bookkeeping. Back from that checkout calls `reopen`,
 * which is the caller's because this modal has been unmounted by then.
 */
const props = defineProps({
	headline: {
		type: String,
		required: true,
	},
	subcopy: {
		type: String,
		default: '',
	},
	reopen: {
		type: Function,
		required: true,
	},
});

const emit = defineEmits(['close', 'stay-free', 'upgrade']);

const subscriptionStore = useSubscriptionPlanStore();
const premiumNudgeStore = usePremiumNudgeStore();

onMounted(() => {
	if (!subscriptionStore.subscriptionPlans?.length) {
		subscriptionStore.initPlans().catch(() => {
			// The price stays a placeholder; the upgrade CTA re-fetches before checkout.
		});
	}
});

const onUpgrade = async (cycle) => {
	if (!subscriptionStore.subscriptionPlans?.length) {
		try {
			await subscriptionStore.initPlans();
		} catch {
			// Surfaced by the checkout flow below.
		}
	}

	const { premiumOptions } = subscriptionStore;
	const option = premiumOptions?.[cycle] ?? premiumOptions?.monthly;

	if (option) {
		subscriptionStore.selectOption(option);
	}

	// Reuse the existing global purchase modal (mounted in App.vue) and Stripe checkout.
	premiumNudgeStore.setShowModal(() => {}, props.reopen);

	// Emit last — the caller's handler typically unmounts this modal.
	emit('upgrade');
};
</script>
